import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-namespace */
import { extend, useThree } from '@react-three/fiber';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { IDomIdSelectors } from '../../../../../typings';
import { onSetCurvatures, onSetMultipleComponentProps_Cn_Doc, onSetPositions, onSetRotations } from '../../../../store/actions';
import { maths } from '../../../../utils/maths';
import { CurvedControlsThree as CurvedControlsThreeImpl } from './CurvedControlsThree';
const CurvedControlsThree = extend(CurvedControlsThreeImpl);
const CurvedControls = (props) => {
    const dispatch = useDispatch();
    const { initialSettings } = props;
    const { camera } = useThree();
    const domElement = document.getElementById(IDomIdSelectors.zapparCanvas);
    const controlsRef = useRef(null);
    const transientPositionRef = useRef(null);
    const transientRotationRef = useRef(null);
    const transientCurvatureRef = useRef(null);
    useLayoutEffect(() => {
        const controlsRefCopy = controlsRef.current;
        return () => {
            controlsRefCopy.dispose();
        };
    }, []);
    const onPointerUp = (mode) => {
        if (!mode)
            return;
        switch (mode) {
            case 'moveVertical':
                if (transientPositionRef.current) {
                    // Update content doc position
                    dispatch(onSetMultipleComponentProps_Cn_Doc([
                        {
                            id: initialSettings.entityId,
                            position: transientPositionRef.current,
                        },
                    ]));
                    // And reset user doc state
                    updateTransientPosition(null);
                }
                break;
            case 'moveAround':
                if (transientPositionRef.current && transientRotationRef.current) {
                    // Update content doc
                    dispatch(onSetMultipleComponentProps_Cn_Doc([
                        {
                            id: initialSettings.entityId,
                            position: transientPositionRef.current,
                            rotation: transientRotationRef.current,
                        },
                    ]));
                    // And reset user doc state
                    updateTransientPosition(null);
                    updateTransientRotation(null);
                }
                break;
            case 'moveAway': {
                if (transientCurvatureRef.current) {
                    // Update content doc
                    dispatch(onSetMultipleComponentProps_Cn_Doc([
                        {
                            id: initialSettings.entityId,
                            position: transientPositionRef.current,
                            curvature: transientCurvatureRef.current,
                        },
                    ]));
                    // And reset user doc state
                    updateTransientPosition(null);
                    updateTransientCurvature(null);
                }
                break;
            }
        }
    };
    const onChange = (mode, data) => {
        switch (mode) {
            case 'moveVertical': {
                const { newPosition } = data;
                updateTransientPosition(newPosition);
                break;
            }
            case 'moveAway':
                {
                    const { newPosition, newCurvature } = data;
                    updateTransientPosition(newPosition);
                    if (newCurvature)
                        updateTransientCurvature(newCurvature);
                }
                break;
            case 'moveAround':
                {
                    const { newPosition, newRotation } = data;
                    updateTransientPosition(newPosition);
                    if (newRotation)
                        updateTransientRotation(newRotation);
                }
                break;
        }
    };
    const updateTransientPosition = (newPosition) => {
        const transientPositionDict = {};
        transientPositionDict[initialSettings.entityId] = newPosition;
        dispatch(onSetPositions(transientPositionDict));
        transientPositionRef.current = newPosition;
    };
    const updateTransientRotation = (newRotation) => {
        const transientRotationDict = {};
        transientRotationDict[initialSettings.entityId] = newRotation ? newRotation.map((v) => maths.toDegrees(v)) : newRotation;
        dispatch(onSetRotations(transientRotationDict));
        transientRotationRef.current = newRotation ? newRotation.map((v) => maths.toDegrees(v)) : newRotation;
    };
    const updateTransientCurvature = (newCurvature) => {
        const transientCurvatureDict = {};
        transientCurvatureDict[initialSettings.entityId] = newCurvature;
        dispatch(onSetCurvatures(transientCurvatureDict));
        transientCurvatureRef.current = newCurvature;
    };
    const controls = useMemo(() => {
        return [
            _jsx(CurvedControlsThree, { renderOrder: Infinity, ref: controlsRef, args: [domElement, camera, initialSettings.entityPosition, initialSettings.entityRotation, initialSettings.curvature, initialSettings.width, initialSettings.isFlatImageTracked, onChange, onPointerUp] }, "CurvedControlsThree"),
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return _jsx(_Fragment, { children: controls });
};
export default CurvedControls;
